import type { TopicData } from "../topic-data";

import { ALL_COLUMNS_ID, LOG_TIME_ID, columnIdForParts } from "./columnId";

/**
 * Rewrite saved `columnFilters` keys from the legacy per-file message-path id to
 * the logical column id ({@link columnIdForParts}), now that the same signal read
 * from several files collapses into one column.
 *
 * Resolves ids against `topicData` rather than a column list, because only the
 * data still carries each message path's per-file id.
 *
 * Idempotent, and returns the input object unchanged (same reference) when no
 * key needs rewriting, so callers can use identity to detect a real change.
 */
export function migratePersistedColumnFilterKeys<V>(
  savedFilters: Record<string, V> | undefined,
  topicData: readonly TopicData[],
): Record<string, V> | undefined {
  if (savedFilters === undefined) {
    return undefined;
  }
  const idToColumnId = new Map<string, string>();
  for (const { messagePath } of topicData) {
    idToColumnId.set(messagePath.id, columnIdForParts(messagePath.parts));
  }
  let rewritten = false;
  const migrated: Record<string, V> = {};
  for (const [key, value] of Object.entries(savedFilters)) {
    const columnId = idToColumnId.get(key);
    if (columnId !== undefined && columnId !== key) {
      rewritten = true;
    }
    migrated[columnId ?? key] = value;
  }
  return rewritten ? migrated : savedFilters;
}

/**
 * Drops persisted column-filter entries whose columns are no longer backed by
 * `data` — removing a data source removes its filters with it.
 *
 * A filter key survives when it is a synthetic column ({@link ALL_COLUMNS_ID} /
 * {@link LOG_TIME_ID}), or either the {@link columnIdForParts} key or the legacy
 * per-file id of a remaining message path. Callers holding a config that may
 * still carry legacy per-file keys must run
 * {@link migratePersistedColumnFilterKeys} against the data as it stood
 * *before* the removal first: a legacy key naming the removed path still
 * describes a column its siblings keep alive, and pruning it here would read as
 * orphaned and silently discard a filter the user can still see.
 *
 * Returns the input object unchanged (same reference) when nothing is pruned,
 * and `undefined` when pruning empties the map.
 */
export function pruneColumnFiltersForData<V>(
  columnFilters: Record<string, V> | undefined,
  data: readonly TopicData[],
): Record<string, V> | undefined {
  if (columnFilters === undefined) {
    return undefined;
  }
  const liveKeys = new Set<string>([ALL_COLUMNS_ID, LOG_TIME_ID]);
  for (const { messagePath } of data) {
    liveKeys.add(messagePath.id);
    liveKeys.add(columnIdForParts(messagePath.parts));
  }
  let pruned = false;
  const next: Record<string, V> = {};
  for (const [key, value] of Object.entries(columnFilters)) {
    if (liveKeys.has(key)) {
      next[key] = value;
    } else {
      pruned = true;
    }
  }
  if (!pruned) {
    return columnFilters;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}
